import { Card, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { IconButton } from "@/components/ui/icon-button";
import { useChildEmailConfirmationConfirm } from "@/orval/child-email-confirmation";
import { createFileRoute } from "@tanstack/react-router";
import { CheckCircle2 } from "lucide-react";
import { useState } from "react";
import { z } from "zod";

const confirmationSearchSchema = z.object({
  childId: z.coerce.number().int().positive().catch(0),
  token: z.string().catch(""),
});

const ConfirmChildEmail = () => {
  const { childId, token } = Route.useSearch();
  const { mutateAsync: confirmChildEmail, isPending } = useChildEmailConfirmationConfirm();
  const [confirmedChildName, setConfirmedChildName] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleConfirm = async () => {
    setError(null);
    try {
      const result = await confirmChildEmail({ data: { childId, token } });
      setConfirmedChildName(result.childName);
    } catch {
      setError("This confirmation link is not valid. Ask a parent to send a new confirmation email.");
    }
  };

  if (!childId || !token) {
    return (
      <div className="flex items-center justify-center flex-1 p-4">
        <Card className="max-w-full w-96">
          <CardHeader>
            <CardTitle>Confirmation link is incomplete</CardTitle>
            <CardDescription>Please ask a parent to send a new confirmation email and try again.</CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-center flex-1 p-4">
      <Card className="max-w-full w-96">
        <CardHeader>
          <CardTitle>{confirmedChildName ? "Email confirmed" : "Confirm child email"}</CardTitle>
          <CardDescription>
            {confirmedChildName
              ? `Your email address has been confirmed for ${confirmedChildName}.`
              : "Confirm this email address for the child."}
          </CardDescription>
          {error && <p className="text-sm text-destructive">{error}</p>}
        </CardHeader>
        {!confirmedChildName && (
          <CardFooter className="p-6 pt-0">
            <IconButton className="w-full" icon={<CheckCircle2 />} onClick={handleConfirm} disabled={isPending}>
              {isPending ? "Confirming..." : "Confirm Email"}
            </IconButton>
          </CardFooter>
        )}
      </Card>
    </div>
  );
};

export const Route = createFileRoute("/account/confirm-child-email")({
  validateSearch: confirmationSearchSchema,
  component: ConfirmChildEmail,
});
